import { useTranslation } from 'react-i18next';
import { SUPPORT_EMAIL } from '@/config/brand';

// Sits above every Suspense boundary, so it must not suspend (useSuspense: false) or the crash
// screen itself would crash and leave a blank page. English defaults cover the case where the
// translations never loaded.
export function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <div role="alert" className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('errors.somethingWentWrong', 'Something went wrong')}</h2>
        <p className="text-gray-600 mb-6">
          {/* supportEmail passed explicitly: this screen can render before i18n's default variables are in place */}
          {t('errors.reloadHint', { defaultValue: 'Please reload the page. If it keeps happening, contact {{supportEmail}}.', supportEmail: SUPPORT_EMAIL })}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            {t('actions.reload', 'Reload page')}
          </button>
          <button
            onClick={resetErrorBoundary}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
          >
            {t('actions.tryAgain', 'Try again')}
          </button>
        </div>
        {import.meta.env.DEV && (
          <details className="mt-4 text-sm text-gray-500">
            <summary>Details</summary>
            <pre className="mt-2 bg-gray-100 p-3 rounded overflow-auto">{error.message}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
